import { css } from '@emotion/react'
import { format } from 'date-fns'
import { ko } from 'date-fns/locale'

import { useStudentAttendances } from '~/@common/hooks/useStudentAttendances'
import { typo } from '~/@common/styles'
import { fontFamily } from '~/@common/styles/fontFamily'
import { mediaQuery } from '~/@common/styles/mediaQuery'
import { Icon } from '~/@common/ui/Icon/Icon'
import InfoTooltip from '~/@common/ui/InfoTooltip/InfoTooltip'

import SectionHeader from '../SectionHeader'

/**
 * 학부모 학습홈 — "오늘의 출석" 섹션
 *
 * ATTENDANCE restriction 가드 안에서만 렌더되도록 부모(ParentHome) 가 제어한다.
 * 그 가드 안에서만 호출되도록 출결 조회 책임도 이 컴포넌트가 보유 —
 * restriction 차단 계정에서는 API 호출 자체가 발생하지 않게 함.
 */
const AttendanceSection = ({
  studentId,
  onPastAttendanceClick,
}: {
  studentId: string
  onPastAttendanceClick: () => void
}) => {
  const today = format(new Date(), 'yyyy-MM-dd')
  const { data: attendances } = useStudentAttendances(studentId, today, today)
  // 오늘 기록의 마지막 entry 만 홈 상단에 표시
  const lastTodayRecord = attendances?.find((d) => d.date === today)?.attendanceList.at(-1) ?? null
  const enterFormatted = formatTime(lastTodayRecord?.enterDatetime ?? null)
  const quitFormatted = formatTime(lastTodayRecord?.quitDatetime ?? null)

  return (
    <div css={_Style}>
      <SectionHeader title="오늘의 출석">
        <InfoTooltip
          title="자녀의 등·하원 시간을 확인할 수 있어요"
          items={['지난 출석 보기를 통해 과거 출석 기록도 조회할 수 있습니다.']}
        />
      </SectionHeader>

      <div className="attendance-container">
        <div className="attendance-cards">
          <div className="attendance-card">
            <span className="attendance-label">등원시간</span>
            <div className="attendance-value-row">
              {enterFormatted ? (
                <>
                  <span className="attendance-time">{enterFormatted.time}</span>
                  <span className="attendance-period">{enterFormatted.period}</span>
                </>
              ) : (
                <span className="attendance-time">--:--</span>
              )}
            </div>
          </div>
          <div className="attendance-divider" />
          <div className="attendance-card">
            <span className="attendance-label">하원시간</span>
            <div className="attendance-value-row">
              {quitFormatted ? (
                <>
                  <span className="attendance-time">{quitFormatted.time}</span>
                  <span className="attendance-period">{quitFormatted.period}</span>
                </>
              ) : (
                <span className="attendance-time">--:--</span>
              )}
            </div>
          </div>
        </div>
        <PastAttendanceLink onClick={onPastAttendanceClick} />
      </div>
    </div>
  )
}

/** 시간 문자열 → { time: '07:15', period: '오후' } */
const formatTime = (datetime: string | null) => {
  if (!datetime) return null
  const date = new Date(datetime)
  return {
    time: format(date, 'hh:mm'),
    period: format(date, 'aaa', { locale: ko }),
  }
}

const PastAttendanceLink = ({ onClick }: { onClick: () => void }) => (
  <button type="button" className="past-attendance-link" onClick={onClick}>
    <span className="past-attendance-text">지난 출석 보기</span>
    <Icon name="icon_chevron_right" size={16} color="var(--Colors-Gray-600)" />
  </button>
)

export default AttendanceSection

const _Style = css`
  display: flex;
  flex-direction: column;

  .attendance-container {
    background: var(--White);
    border-radius: var(--Radius-300);
    display: flex;
    flex-direction: column;
    height: 153px;
    ${mediaQuery.underTablet} {
      height: 135px;
    }
  }

  .attendance-cards {
    display: flex;
    flex: 1;
    align-items: center;
  }

  .attendance-card {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 14px 0;
  }

  .attendance-label {
    ${typo.body02};
    color: var(--Colors-Gray-800);
  }

  .attendance-value-row {
    display: flex;
    align-items: center;
    gap: 4px;

    ${mediaQuery.underDesktop} {
      flex-direction: column;
      gap: 4px;
    }
    ${mediaQuery.underTablet} {
      flex-direction: row;
    }
  }

  .attendance-time {
    ${fontFamily.montserrat};
    font-size: 36px;
    font-weight: 700;
    line-height: 36px;
    color: var(--Colors-Gray-900);
  }

  .attendance-period {
    ${typo.body02};
    color: var(--Colors-Gray-800);
  }

  .attendance-divider {
    width: 1px;
    height: 50px;
    background: var(--Colors-Gray-200);
  }

  .past-attendance-link {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: 10px;
    background: none;
    border: none;
    border-top: 1px solid var(--Colors-Gray-200);
    cursor: pointer;
  }

  .past-attendance-text {
    ${typo.caption01};
    color: var(--Colors-Gray-600);
  }
`
